/**
 * InsuranceQuestionsCard - Phase 3.2 Accountant Dashboard Enhancement
 * 
 * Shows AI-generated likely adjuster questions with pre-populated answers.
 * Allows copy-to-clipboard for quick responses.
 */

import React, { useEffect, useState } from 'react';
import { useAuth } from '../../contexts/TestAuthContext';
import { api } from '../../utils/api';
import {
  MessageCircleQuestion,
  Copy,
  Check,
  ChevronDown,
  ChevronUp,
  Loader2,
  Sparkles,
  BookOpen
} from 'lucide-react';

interface InsuranceQuestion {
  id: string;
  question: string;
  suggested_answer: string;
  category: 'revenue' | 'expenses' | 'causation' | 'documentation';
  confidence: number;
  sources: string[];
}

interface InsuranceQuestionsCardProps {
  reportId: string;
  className?: string;
}

const CATEGORY_STYLES = {
  revenue: { bg: 'bg-green-100', text: 'text-green-700' },
  expenses: { bg: 'bg-blue-100', text: 'text-blue-700' },
  causation: { bg: 'bg-purple-100', text: 'text-purple-700' },
  documentation: { bg: 'bg-orange-100', text: 'text-orange-700' }
};

export default function InsuranceQuestionsCard({ reportId, className = '' }: InsuranceQuestionsCardProps) {
  const { getToken } = useAuth();
  const [questions, setQuestions] = useState<InsuranceQuestion[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [copied, setCopied] = useState<string | null>(null);

  useEffect(() => {
    if (reportId) fetchQuestions();
  }, [reportId]);

  const fetchQuestions = async () => {
    setLoading(true);
    setError(false);
    try {
      const token = await getToken();
      const data = await api.get(`/api/v1/dashboard/insurance-questions/${reportId}`, {
        headers: { Authorization: `Bearer ${token}` },
        showErrorToast: false
      });
      setQuestions(data.questions || []);
    } catch (err) {
      console.debug('Insurance questions API not available');
      setQuestions([]);
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = async (text: string, questionId: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(questionId);
      setTimeout(() => setCopied(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  if (loading) {
    return (
      <div className={`bg-white rounded-xl shadow-sm border border-gray-200 p-6 ${className}`}>
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
        </div>
      </div>
    );
  }

  return (
    <div className={`bg-white rounded-xl shadow-sm border border-gray-200 ${className}`}>
      {/* Header */}
      <div className="p-4 border-b border-gray-100">
        <div className="flex items-center">
          <MessageCircleQuestion className="w-5 h-5 text-blue-600 mr-2" />
          <h3 className="text-lg font-semibold text-gray-900">Likely Adjuster Questions</h3>
          <span className="ml-2 px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded-full flex items-center">
            <Sparkles className="w-3 h-3 mr-1" />
            AI Generated
          </span>
        </div>
        <p className="text-sm text-gray-500 mt-1">
          Pre-populated answers based on claim data
        </p>
      </div>

      {/* Questions list */}
      <div className="divide-y divide-gray-100">
        {error && questions.length === 0 ? (
          <div className="p-6 text-center">
            <MessageCircleQuestion className="w-8 h-8 mx-auto mb-2 text-slate-400" />
            <p className="text-sm text-slate-500">Questions unavailable</p>
            <button
              onClick={fetchQuestions}
              className="mt-2 text-xs text-blue-600 hover:text-blue-700"
            >
              Retry
            </button>
          </div>
        ) : questions.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            <MessageCircleQuestion className="w-10 h-10 mx-auto mb-2 text-gray-400" />
            <p>No questions generated yet</p>
          </div>
        ) : (
          questions.map((q) => {
            const categoryStyle = CATEGORY_STYLES[q.category] || CATEGORY_STYLES.documentation;
            const isExpanded = expanded === q.id;

            return (
              <div key={q.id} className="p-4">
                <button
                  onClick={() => setExpanded(isExpanded ? null : q.id)}
                  className="w-full text-left"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center mb-2">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${categoryStyle.bg} ${categoryStyle.text}`}>
                          {q.category}
                        </span>
                        <span className="ml-2 text-xs text-gray-500">
                          {Math.round(q.confidence * 100)}% confidence
                        </span>
                      </div>
                      <h4 className="font-medium text-gray-900">
                        {q.question}
                      </h4>
                    </div>
                    {isExpanded ? (
                      <ChevronUp className="w-5 h-5 text-gray-400 ml-2 flex-shrink-0" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-gray-400 ml-2 flex-shrink-0" />
                    )}
                  </div>
                </button>

                {/* Expanded answer */}
                {isExpanded && (
                  <div className="mt-4">
                    <div className="bg-gray-50 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-gray-700">Suggested Answer</span>
                        <button
                          onClick={() => copyToClipboard(q.suggested_answer, q.id)}
                          className="flex items-center px-2 py-1 text-sm text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded transition-colors"
                        >
                          {copied === q.id ? (
                            <>
                              <Check className="w-4 h-4 mr-1" />
                              Copied!
                            </>
                          ) : (
                            <>
                              <Copy className="w-4 h-4 mr-1" />
                              Copy
                            </>
                          )}
                        </button>
                      </div>
                      <p className="text-sm text-gray-700 leading-relaxed">
                        {q.suggested_answer}
                      </p>
                    </div>

                    {/* Sources */}
                    {q.sources.length > 0 && (
                      <div className="mt-3 flex items-center text-xs text-gray-500">
                        <BookOpen className="w-3 h-3 mr-1" />
                        Sources: {q.sources.join(', ')}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
